/**
 * All ReactQuery related functionality for Actions
 */

import { queryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query'

import { successStaleTime } from '@/shared/api/utils/query'
import { useCurrentTenant } from '@/shared/auth/store'
import { time } from '@/shared/datetime/utils/time'

import { fetchActionDetails, fetchActionList } from './api'

import type { Action } from './types'

type ActionFilters = Record<
	string,
	string | boolean | undefined | Record<string, string>
>

/**
 * Query keys for Actions
 * - list: Fetch all actions
 * - details: Fetch a single action
 * - run: Run an action
 */
export const actionsKeys = {
	all: ['actions'] as const,

	lists: () => [...actionsKeys.all, 'list'] as const,
	list: (tenant: string, filters?: ActionFilters) =>
		[...actionsKeys.lists(), tenant, filters] as const,

	details: () => [...actionsKeys.all, 'details'] as const,
	detail: (tenant: string, id: string) =>
		[...actionsKeys.details(), tenant, id] as const,

	run: (id: string) => [...actionsKeys.all, 'run', id] as const,
}

/**
 * Query Options for Actions
 *
 * Used for fetching data on both a custom hook and a query client (preload)
 */
export const actionQueries = {
	/**
	 * Query options for fetching all actions
	 * @param tenant - Tenant ID
	 * @param filters - Filters to apply
	 * @param select - Selector to apply
	 */
	list: (
		tenant: string,
		filters?: ActionFilters,
		select?: (data: Array<Action>) => Array<Action>,
	) =>
		queryOptions({
			queryKey: actionsKeys.list(tenant, filters),
			queryFn: ({ signal }) => fetchActionList(tenant, signal),
			gcTime: time(1, 'h'),
			staleTime: ({ state }) => successStaleTime(state, 1, 'h'),
			select: select ?? ((data) => data),
		}),

	/**
	 * Query options for fetching the details of an action
	 * @param tenant - Tenant ID
	 * @param id - Action ID
	 */
	detail: (tenant: string, id: string) =>
		queryOptions({
			queryKey: actionsKeys.detail(tenant, id),
			queryFn: ({ signal }) => fetchActionDetails(tenant, id, signal),
			gcTime: time(1, 'h'),
			staleTime: ({ state }) => successStaleTime(state, 1, 'h'),
		}),
}

/**
 * Custom hook to fetch all actions using ReactQuery
 * @param filters - Filters to apply
 * @param select - Selector to apply
 */
export const useActionList = (
	filters?: ActionFilters,
	select?: (data: Array<Action>) => Array<Action>,
) => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery({
		...actionQueries.list(tenant, filters, select),
	})
}

/**
 * Custom hook to fetch the details of an action using ReactQuery
 * @param id - Action ID
 */
export const useActionDetails = (id: string) => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery({
		...actionQueries.detail(tenant, id),
	})
}

/**
 * Non-suspending variant of `useActionDetails`. Returns an idle/loading state
 * instead of suspending and is gated on `tenant`/`id`, so prefer it when the
 * action ID may be absent or when the caller must not be wrapped in Suspense
 * (e.g. conditional/optional detail fetches). Use `useActionDetails` for the
 * Suspense-driven path where the ID is guaranteed.
 */
export const useActionDetailsSync = (id: string) => {
	const tenant = useCurrentTenant()

	return useQuery({
		...actionQueries.detail(tenant, id),
		enabled: !!tenant && !!id,
	})
}
